import * as React from 'react'
import { Query } from 'react-apollo'

import { LOCIZE_NAMESPACE } from 'main/common'
import AuthModal, { Step as AuthModalStep } from 'main/components/common/AuthModal'
import { IWhitelabelContext, Networks, useWhitelabel } from 'main/framework'
import { CredentialsFields } from 'main/gql/credentials'
import { User, USER_QUERY, User_user } from 'main/gql/user'
import useAppFeatureFlags from 'main/hooks/useAppFeatureFlags'
import useSSOContext, { ExtendNavContext, INavContext } from 'main/hooks/useSSOContext'
import { directorDashboardHomePath, directorDashboardWebsitePath } from 'main/routes'
import { Hidden, Visible } from 'main/ui'
import { AssetName } from 'main/util/asset'
import { IPageAlert } from 'main/util/page'
import { userType, UserType } from 'main/util/user'
import { TFunction, useTranslation } from 'react-i18next'
import { getAppConfig } from 'sdk/sso/config/env'

import Hamburger from './Hamburger'
import {
  Container,
  ContentContainer,
  LeftAndRightContainer,
  LeftContainer,
  MainContentContainer,
  MenuBackdrop,
  MenuContainer,
  RightContainer,
  SearchFormContainer,
  TitleContainer,
} from './Header.styles'
import LanguageSelection from './LanguageSelection/'
import LinkedLogo from './LinkedLogo'
import Links from './Links'
import Menu from './Menu'
import PageAlert from './PageAlert'
import Profile from './Profile'
import SearchForm from './SearchForm'
import { ISearchProps } from './types'

interface IProps {
  bottomBar?: React.ReactNode
  pageAlert: IPageAlert | null
  onDismissPageAlert: () => void
  alwaysShowSearchForm?: boolean
  alwaysSticky?: boolean
  isBannerActive?: boolean
  currentSearch?: ISearchProps
  customRight?: React.ReactElement
  onlyShowLogo?: boolean
  disableLogoLink?: boolean
  disableAuthModal?: boolean
  externalOnboardingEnabled: boolean
}

interface IPropsInner extends IProps {
  t: TFunction
  navContext?: INavContext
  extendNavContext?: ExtendNavContext
  whitelabel: IWhitelabelContext
}

interface IAuthModalState {
  initialStep: AuthModalStep
}

interface IState {
  authModal: IAuthModalState | null
  menuActive: boolean
  sticky: boolean
}

class Header extends React.Component<IPropsInner, IState> {
  public state: IState = {
    authModal: null,
    menuActive: false,
    sticky: this.sticky,
  }

  public componentDidMount() {
    window.addEventListener('scroll', this.handleScroll)
    this.updateAuthModalOnboarding()
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  public componentWillUpdate() {
    this.updateAuthModalOnboarding()
  }

  public render() {
    return (
      <Query<User> query={USER_QUERY}>
        {({ data }) => this.renderContent(data ? data.user : null)}
      </Query>
    )
  }

  private renderContent(user: User_user | null) {
    const {
      bottomBar,
      customRight,
      pageAlert,
      alwaysShowSearchForm,
      isBannerActive,
      currentSearch,
      onlyShowLogo,
      disableLogoLink,
      disableAuthModal,
      t,
    } = this.props
    const { authModal, menuActive, sticky } = this.state
    const showSearchForm = sticky || alwaysShowSearchForm || false
    const whitelabel = this.props.whitelabel
    const usingNewMexico = whitelabel.networkAbbr === Networks.NM

    return (
      <Container css={{ marginTop: isBannerActive ? '64px' : 0 }}>
        <ContentContainer sticky={sticky}>
          <MainContentContainer
            backgroundColor={whitelabel.getAsset(AssetName.LOGO_AND_SEARCH_BACKGROUND_COLOR)}
          >
            <LeftAndRightContainer>
              <LeftContainer>
                <LinkedLogo disableLogoLink={disableLogoLink} showSearchForm={showSearchForm} />

                {showSearchForm && !onlyShowLogo && (
                  <Hidden xs sm>
                    <SearchFormContainer>
                      <SearchForm currentSearch={currentSearch} />
                    </SearchFormContainer>
                  </Hidden>
                )}

                {usingNewMexico && <TitleContainer>{t('NETWORK-NM-SEARCH-TITLE')}</TitleContainer>}
              </LeftContainer>
              {!onlyShowLogo && this.renderCustomMaybe(customRight, user)}
            </LeftAndRightContainer>
            {showSearchForm && !onlyShowLogo && (
              <Hidden md lg xl>
                <SearchFormContainer>
                  <SearchForm currentSearch={currentSearch} />
                </SearchFormContainer>
              </Hidden>
            )}
            {!disableAuthModal && authModal && this.renderAuthModal(authModal)}
            {menuActive && this.renderMenu(user)}
            {pageAlert && this.renderAlert(pageAlert)}
          </MainContentContainer>
          {bottomBar}
        </ContentContainer>
      </Container>
    )
  }

  private renderCustomMaybe(customRight: React.ReactElement | undefined, user: User_user | null) {
    if (customRight) return customRight
    return (
      <RightContainer>
        <Visible lg xl>
          <LanguageSelection user={user} />
          <Links user={user} onRequestOpenAuthModal={this.handleRequestOpenAuthModal} />
        </Visible>

        {this.renderActions(user)}
      </RightContainer>
    )
  }

  private renderActions(user: User_user | null) {
    const { menuActive } = this.state

    if (user) {
      return (
        <>
          <Hidden lg xl>
            <LanguageSelection user={user} />
          </Hidden>
          <Profile menuActive={menuActive} user={user} onClickMenu={this.handleMenuClick} />
        </>
      )
    }

    return (
      <Hidden lg xl>
        <LanguageSelection user={user} />
        <Hamburger onClick={this.handleMenuClick} />
      </Hidden>
    )
  }

  private updateAuthModalOnboarding() {
    if (!this.state.authModal && this.props.navContext?.isOnboarding) {
      this.handleRequestOpenAuthModal('parent_onboarding')
    }
  }

  private renderAuthModal(authModal: IAuthModalState) {
    const { initialStep } = authModal
    const isOnboarding = initialStep === 'parent_onboarding'
    return (
      <AuthModal
        isOnboarding={isOnboarding}
        initialStep={initialStep}
        onAuth={this.redirectToDirectorDashboardMaybe}
        onRequestClose={this.handleRequestCloseAuthModal}
      />
    )
  }

  private renderMenu(user: User_user | null) {
    if (user) return this.renderMenuContent(user)

    return (
      <Hidden lg xl>
        {this.renderMenuContent(null)}
      </Hidden>
    )
  }

  private renderMenuContent(user: User_user | null) {
    return (
      <React.Fragment>
        <MenuBackdrop onClick={this.handleMenuBackdropClick} />
        <MenuContainer>
          <Menu user={user} onRequestOpenAuthModal={this.handleRequestOpenAuthModal} />
        </MenuContainer>
      </React.Fragment>
    )
  }

  private renderAlert(pageAlert: IPageAlert) {
    const { onDismissPageAlert } = this.props

    return <PageAlert {...pageAlert} onDismiss={onDismissPageAlert} />
  }

  private handleMenuClick = () => {
    this.setState(({ menuActive }) => ({ menuActive: !menuActive }))
  }

  private handleMenuBackdropClick = () => {
    this.setState({ menuActive: false })
  }

  private handleScroll = () => {
    this.setState({ sticky: this.sticky })
  }

  private handleRequestOpenAuthModal = (initialStep: AuthModalStep) => {
    const {
      externalOnboardingEnabled,
      whitelabel: { isWhitelabeled },
    } = this.props

    const notInLoginOrOnboarding = !['login', 'parent_onboarding'].includes(initialStep)
    const redirectToOnboarding =
      externalOnboardingEnabled && !isWhitelabeled && notInLoginOrOnboarding

    if (redirectToOnboarding) {
      window.location.href = getAppConfig().onboardingDomain
      return
    }

    this.setState({ authModal: { initialStep } })
  }

  private handleRequestCloseAuthModal = () => {
    // finish authReturn onboarding
    if (this.props.navContext?.isOnboarding && this.props.extendNavContext) {
      this.props.extendNavContext({
        isOnboarding: false, // make sure to set this before modal closes or else it will reopen
      })
    }
    this.setState({ authModal: null })
  }

  private redirectToDirectorDashboardMaybe = (credentials: CredentialsFields) => {
    if (userType(credentials.user) === UserType.Director) {
      const migratedToWsCenters = credentials.user?.schoolhouse?.migratedToWsCenters || false
      const directorHomePath = migratedToWsCenters
        ? directorDashboardWebsitePath()
        : directorDashboardHomePath()

      window.location.replace(directorHomePath)
    }
  }

  private get sticky() {
    if (this.props.alwaysSticky) {
      return true
    } else {
      return window.pageYOffset > 0
    }
  }
}

function HeaderWithNavContext(props: IProps) {
  const { navContext, extendNavContext } = useSSOContext()
  const whitelabel = useWhitelabel()
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const externalOnboardingEnabled =
    useAppFeatureFlags().appFeatureFlagEnabled('external_onboarding')
  return (
    <Header
      {...props}
      navContext={navContext}
      extendNavContext={extendNavContext}
      whitelabel={whitelabel}
      externalOnboardingEnabled={externalOnboardingEnabled}
      t={t}
    />
  )
}

export default HeaderWithNavContext
